import React from "react";
import Quagga from "@ericblade/quagga2";
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
} from "mdbreact";

const READERS = [
  "ean_reader",
  "ean_8_reader",
  "upc_reader",
  "upc_e_reader",
  "code_128_reader",
  "code_39_reader",
  "code_93_reader",
  "codabar_reader",
  "i2of5_reader",
];

const isLocalHost = () => {
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
};

const isCameraSecureContext = () => {
  return window.isSecureContext || isLocalHost();
};

/* Lo que decide si se puede escanear en vivo es que exista getUserMedia, no
   que el contexto esté marcado como seguro. No es lo mismo: dentro del
   WebView de la app Android (Capacitor cargando sandbox.xflowpro.com por
   https, en un iframe de tformularios también por https) isSecureContext
   viene en false, así que el guard viejo desviaba al <input capture> y se
   abría la cámara del sistema a pantalla completa — que no lee códigos,
   solo saca fotos. En el navegador del mismo teléfono, con la misma URL,
   venía true y por eso ahí sí escaneaba.

   Preguntar por la API es además más honesto: si getUserMedia existe pero
   el navegador la rechaza, el error real aparece en el modal en vez de
   quedar tapado por una suposición nuestra. Y si de verdad no se puede,
   el botón "Tomar foto" del modal sigue siendo la salida manual. */
const hasLiveCameraApi = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

const getCameraErrorMessage = (error) => {
  /* Solo cuando no hay un error concreto que mostrar: en el WebView de la app
     el contexto figura como inseguro aunque todo se sirva por https, así que
     este mensaje tapaba la causa real (permiso denegado, cámara ocupada, etc.)
     con una explicación equivocada. Si hay error, manda el error. */
  if (!error && !isCameraSecureContext()) {
    return "La camara solo funciona con HTTPS o localhost. En celular abre la pagina con una URL https.";
  }

  const errorText =
    typeof error === "string"
      ? error
      : error && (error.name || error.message)
      ? error.name || error.message
      : "";

  if (errorText.indexOf("NotAllowed") >= 0 || errorText.indexOf("Permission") >= 0) {
    return "Permiso de camara denegado. Activa el permiso de camara para esta pagina.";
  }

  if (errorText.indexOf("NotFound") >= 0 || errorText.indexOf("Devices") >= 0) {
    return "No se encontro una camara disponible en el dispositivo.";
  }

  if (errorText.indexOf("NotReadable") >= 0 || errorText.indexOf("TrackStart") >= 0) {
    return "La camara esta siendo usada por otra aplicacion o no se pudo iniciar.";
  }

  if (errorText.indexOf("Security") >= 0) {
    return "El navegador bloqueo la camara por seguridad. Usa HTTPS o localhost.";
  }

  return errorText || "No se pudo iniciar la camara.";
};

class BarcodeScanner extends React.Component {
  scannerRef = React.createRef();
  fileInputRef = React.createRef();
  lastCodes = [];
  isDetecting = false;
  processedFrames = 0;

  state = {
    isOpen: false,
    isScanning: false,
    error: "",
    frames: 0,
  };

  componentWillUnmount() {
    this.stopScanner();
  }

  waitForScannerElement = () => {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const findElement = () => {
        const element = this.scannerRef.current;

        if (element) {
          resolve(element);
          return;
        }

        attempts += 1;

        if (attempts > 30) {
          reject(new Error("No se encontro el contenedor del lector."));
          return;
        }

        window.setTimeout(findElement, 100);
      };

      findElement();
    });
  };

  getLiveConfig = (target) => ({
    inputStream: {
      type: "LiveStream",
      target,
      constraints: {
        facingMode: "environment",
        width: { min: 640, ideal: 1280 },
        height: { min: 480, ideal: 720 },
      },
      area: {
        top: "20%",
        right: "4%",
        left: "4%",
        bottom: "20%",
      },
    },
    locator: {
      patchSize: "large",
      halfSample: false,
    },
    numOfWorkers: navigator.hardwareConcurrency
      ? Math.min(navigator.hardwareConcurrency, 4)
      : 2,
    frequency: 10,
    decoder: {
      readers: READERS,
      multiple: false,
    },
    locate: true,
  });

  openScanner = () => {
    if (this.props.disabled) {
      return;
    }

    if (!hasLiveCameraApi() && this.fileInputRef.current) {
      this.fileInputRef.current.click();
      return;
    }

    this.setState(
      {
        isOpen: true,
        isScanning: true,
        error: "",
        frames: 0,
      },
      this.startScanner
    );
  };

  closeScanner = () => {
    this.stopScanner();
    this.setState({
      isOpen: false,
      isScanning: false,
      error: "",
      frames: 0,
    });
  };

  stopScanner = () => {
    this.isDetecting = false;
    this.lastCodes = [];
    this.processedFrames = 0;

    try {
      Quagga.offDetected(this.handleDetected);
      Quagga.offProcessed(this.handleProcessed);
      Quagga.stop();
    } catch (error) {
      console.log("Barcode scanner stop error", error);
    }
  };

  startScanner = async () => {
    if (!hasLiveCameraApi()) {
      this.setState({
        isScanning: false,
        error: getCameraErrorMessage(),
      });
      return;
    }

    try {
      const target = await this.waitForScannerElement();

      this.isDetecting = true;
      this.lastCodes = [];
      this.processedFrames = 0;

      Quagga.offDetected(this.handleDetected);
      Quagga.offProcessed(this.handleProcessed);

      await Quagga.init(this.getLiveConfig(target), (error) => {
        if (error) {
          this.setState({
            isScanning: false,
            error: getCameraErrorMessage(error),
          });
          return;
        }

        Quagga.onProcessed(this.handleProcessed);
        Quagga.onDetected(this.handleDetected);
        Quagga.start();
      });
    } catch (error) {
      this.setState({
        isScanning: false,
        error: getCameraErrorMessage(error),
      });
    }
  };

  handleProcessed = () => {
    this.processedFrames += 1;

    if (this.processedFrames % 10 === 0) {
      this.setState({ frames: this.processedFrames });
    }
  };

  handleDetected = (result) => {
    if (!this.isDetecting || !result || !result.codeResult) {
      return;
    }

    const code = result.codeResult.code;
    const format = result.codeResult.format;

    console.log("SKU scan detected", code, format);

    if (!code || code.length < 8) {
      return;
    }

    this.lastCodes.push(code);
    this.lastCodes = this.lastCodes.slice(-3);

    const repeatedReads = this.lastCodes.filter((item) => item === code).length;

    if (repeatedReads < 2) {
      this.setState({
        error: "Confirmando lectura: " + code,
      });
      return;
    }

    this.isDetecting = false;
    this.props.onDetected(code);
    this.closeScanner();
  };

  openImageCapture = () => {
    if (this.props.disabled || !this.fileInputRef.current) {
      return;
    }

    this.fileInputRef.current.click();
  };

  handleImageSelected = async (event) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    const imageUrl = URL.createObjectURL(file);

    this.setState({
      isOpen: true,
      isScanning: true,
      error: "Procesando imagen...",
      frames: 0,
    });

    try {
      this.stopScanner();

      Quagga.decodeSingle(
        {
          src: imageUrl,
          numOfWorkers: 0,
          inputStream: {
            size: 1280,
          },
          locator: {
            patchSize: "large",
            halfSample: false,
          },
          decoder: {
            readers: READERS,
            multiple: false,
          },
          locate: true,
        },
        (result) => {
          URL.revokeObjectURL(imageUrl);

          if (result && result.codeResult && result.codeResult.code) {
            this.props.onDetected(result.codeResult.code);
            this.closeScanner();
            return;
          }

          this.setState({
            isScanning: false,
            error:
              "No se pudo leer el codigo en la imagen. Intenta acercar mas el codigo y enfocar bien.",
          });
        }
      );
    } catch (error) {
      URL.revokeObjectURL(imageUrl);
      this.setState({
        isScanning: false,
        error:
          "No se pudo leer el codigo en la imagen. Intenta acercar mas el codigo y enfocar bien.",
      });
    } finally {
      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = "";
      }
    }
  };

  render() {
    return (
      <>
        <MDBBtn
          size="sm"
          color="primary"
          className="barcode-scan-btn"
          type="button"
          disabled={this.props.disabled}
          onClick={this.openScanner}
        >
          <MDBIcon icon="camera" />
        </MDBBtn>
        <input
          ref={this.fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={this.handleImageSelected}
        />

        <MDBModal
          centered
          isOpen={this.state.isOpen}
          toggle={this.closeScanner}
          size="md"
        >
          <MDBModalHeader toggle={this.closeScanner}>
            Escanear SKU
          </MDBModalHeader>
          <MDBModalBody>
            <div className="barcode-scanner-video-wrap">
              <div ref={this.scannerRef} className="barcode-scanner-video" />
              <div className="barcode-scanner-frame" />
            </div>
            <div className="barcode-scanner-status">
              {this.state.error ||
                (this.state.isScanning
                  ? "Buscando codigo... Frames: " + this.state.frames
                  : "Iniciando camara...")}
            </div>
            {/* Cancelar primero: es la salida, y en el pulgar de una mano la
                zona izquierda es la de menor riesgo de tocar por error. */}
            <div className="barcode-scanner-actions">
              <MDBBtn size="sm" color="secondary" onClick={this.closeScanner}>
                Cancelar
              </MDBBtn>
              <MDBBtn
                size="sm"
                color="primary"
                onClick={this.openImageCapture}
                disabled={this.props.disabled}
              >
                Tomar foto
              </MDBBtn>
            </div>
          </MDBModalBody>
        </MDBModal>
      </>
    );
  }
}

export default BarcodeScanner;
